<template lang="pug">
  BForm(@submit.prevent="onSubmit" @keyup="clearError" ref="form")
    h5 {{ title }}
    BRow
      BCol(xl="8")
        BCard
          BFormGroup(
            v-for="field in form"
            :key="field.code"
            :label="$t(`forms.fields.${field.code}`)"
          )
            template(v-if="field.type === 'text'")
              BFormInput(
                :name="field.code"
                v-model="item[field.code]"
                v-validate="field.validation"
                :data-vv-as="$t(`forms.fields.${field.code}`)"
                :state="!isError(field.code)"
              )
              BFormInvalidFeedback
                | {{ getError(field.code) }}
            template(v-if="field.type === 'textarea'")
              VueEditor(
                v-model="item[field.code]"
                :editorToolbar="editorToolbar"
              )
              BTextarea(
                :name="field.code"
                v-model="item[field.code]"
                v-validate="field.validation"
                :data-vv-as="$t(`forms.fields.${field.code}`)"
                :state="!isError(field.code)"
                hidden
              )
              BFormInvalidFeedback
                | {{ getError(field.code) }}
            template(v-if="field.type === 'boolean'")
              BFormInput(
                hidden
                :name="field.code"
                value="0"
              )
              BFormCheckbox.mb-2(
                :name="field.code"
                v-model="item[field.code]"
                value="1"
                switch
              )
            template(v-if="field.type === 'select'")
              BFormSelect(
                :name="field.code"
                :options="field.options"
                v-model="item[field.code]"
                v-validate="field.validation"
                :data-vv-as="$t(`forms.fields.${field.code}`)"
              )
                template(v-slot:first)
                  option(:value="null") -- выбрать --
              BFormInvalidFeedback(:state="!isError(field.code)")
                | {{ getError(field.code) }}
            template(v-if="field.type === 'multiselect'")
              BFormSelect(
                multiple
                :name="`${field.code}[]`"
                :options="field.options"
                v-model="item[field.code]"
                v-validate="field.validation"
                :data-vv-as="$t(`forms.fields.${field.code}`)"
                :select-size="field.size"
              )
                template(v-slot:first)
                  option(:value="null") -- выбрать --
              BFormInvalidFeedback(:state="!isError(field.code)")
                | {{ getError(field.code) }}
            template(v-if="field.type === 'date'")
              DatePicker(
                :input-name="field.code"
                v-model="item[field.code]"
                v-validate="field.validation"
                :data-vv-name="field.code"
                format="DD.MM.YYYY"
                value-type="format"
                :first-day-of-week="1"
                lang="ru"
              )
            template(v-if="field.type === 'array'")
              template(v-for="key in field.keys")
                BFormGroup(
                  label-align-md
                  label-align-sm="right"
                  label-cols-sm="3"
                  :label="key.label"
                )
                  BFormInput.mb-2(
                    v-if="edit"
                    :name="`${field.code}[${key.code}]`"
                    v-model="item[field.code][key.code]"
                    v-validate="key.validation"
                    :data-vv-as="$t(`forms.fields.${key.code}`)"
                    :state="!isError(`${field.code}.${key.code}`)"
                  )
                  BFormInput.mb-2(
                    v-else
                    :name="`${field.code}[${key.code}]`"
                    v-validate="key.validation"
                    :data-vv-as="$t(`forms.fields.${key.code}`)"
                    :state="!isError(`${field.code}.${key.code}`)"
                  )
                  BFormInvalidFeedback
                    | {{ getError(`${field.code}.${key.code}`) }}
            template(v-if="field.type === 'locales'")
              BFormCheckbox(
                v-for="lang in $i18n.availableLocales"
                :key="lang"
                :name="`${field.code}[]`"
                v-model="item[field.code]"
                :value="lang"
                switch
              )
                img(:src=`require('@/assets/img/flags/' + lang + '.png')`)
            template(v-if="field.type === 'file'")
              div.mb-2(v-if="item[field.code + '_old']")
                a(:href="item[field.code + '_old']" target="_blank")
                  | {{ item[field.code + '_old'] }}
              BFormCheckbox.mb-2(
                :name="field.code + '_delete'"
                value="1"
                switch
                v-model="item[field.code + '_delete']"
                v-if="item[field.code + '_old']"
              ) {{ $t('forms.file.delete') }}
              BFormFile(
                :name="field.code"
                :placeholder="$t('forms.file.placeholder')"
                :browse-text="$t('forms.file.change')"
                v-model="item[field.code]"
                v-validate="field.validation"
                :data-vv-as="$t(`forms.fields.${field.code}`)"
              )
              BFormInvalidFeedback(:state="!isError(field.code)")
                | {{ getError(field.code) }}
            template(v-if="field.type === 'image'")
              div.mb-2(v-if="item[field.code + '_old']")
                a(:href="item[field.code + '_old']" target="_blank")
                  BImg.mb-2(
                    height="200"
                    rounded
                    :src="item[field.code + '_old']"
                  )
              BFormCheckbox.mb-2(
                :name="field.code + '_delete'"
                value="1"
                switch
                v-model="item[field.code + '_delete']"
                v-if="item[field.code + '_old']"
              ) {{ $t('forms.file.delete') }}
              BFormFile(
                :name="field.code"
                :placeholder="$t('forms.file.placeholder')"
                :browse-text="$t('forms.file.change')"
                v-model="item[field.code]"
                v-validate="field.validation"
                :data-vv-as="$t(`forms.fields.${field.code}`)"
              )
              BFormInvalidFeedback(:state="!isError(field.code)")
                | {{ getError(field.code) }}
          template(v-slot:footer)
            BButton.mr-2.mb-2(
              type="submit"
              variant="success"
              :disabled="disabledButton"
              @click="backAfterSave = true"
            ) {{ $t('forms.buttons.save') }}
            BButton.mr-2.mb-2(
              type="submit"
              variant="info"
              :disabled="disabledButton"
              @click="backAfterSave = false"
            ) {{ $t('forms.buttons.apply') }}
            RouterLink.btn.mr-2.mb-2(
              tag="button"
              :to="{name: routes.index}"
            ) {{ $t('forms.buttons.cancel') }}
</template>

<script>
import validation from '@/mixins/validation'
import DatePicker from 'vue2-datepicker'
import { VueEditor } from 'vue2-editor'

export default {
  components: {
    DatePicker,
    VueEditor
  },
  mixins: [validation],
  props: {
    title: { type: String, required: true },
    routeKey: { type: String, required: true },
    routes: { type: Object, required: true },
    form: { type: Array, required: true },
    data: { type: Object, required: false },
    create: { type: Function, required: true },
    update: { type: Function, required: true }
  },
  data: () => ({
    backAfterSave: false,
    editorToolbar: [
      [{ 'header': [false, 1, 2, 3] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote'],
      [
        { color: ['#FFFFFF', '#131929', '#009BE2', '#66C902', '#D20C3C', '#9D9FB5', '#6A6D8E', '#844F9C'] },
        { background: ['#FFFFFF', '#131929', '#009BE2', '#66C902', '#D20C3C', '#9D9FB5', '#6A6D8E', '#844F9C'] }
      ],
      ['link'],
      ['clean']
    ]
  }),
  computed: {
    item: vm => vm.data,
    edit: vm => !!vm.data.id
  },
  methods: {
    async onSubmit () {
      this.formValidation.saving = true

      try {
        if (this.edit) {
          await this.onUpdate()
        } else {
          await this.onCreate()
        }

        this.$notify({ message: this.$t('forms.notify.saved') })
      } catch (error) {
        if (error.response?.status === 422) {
          this.formValidation.errors = error.response?.data?.error?.detail
        }
      }

      this.formValidation.saving = false
    },
    async onUpdate () {
      await this.update({
        route: this.$route,
        form: new FormData(this.$refs.form)
      })

      if (this.backAfterSave) {
        await this.$router.push({ name: this.routes.index })
      }
    },
    async onCreate () {
      await this.create({
        route: this.$route,
        form: new FormData(this.$refs.form)
      })

      if (this.backAfterSave) {
        await this.$router.push({ name: this.routes.index })
      } else {
        await this.$router.push({
          name: this.routes.edit,
          params: {
            [this.routeKey]: this.item.id
          }
        })
      }
    }
  }
}
</script>
